import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { logError } from './errorLogger';

// -------------------------------
// Base URL resolver
// -------------------------------

function resolveRawBase(): string {
  // 1) If explicitly set via env at build time, use it
  const envBase = process.env.REACT_APP_API_BASE_URL?.trim() || '';
  if (envBase && /^https?:\/\//i.test(envBase)) return envBase;
  
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';
  if (isDevelopment) {
    console.log('🔧 Running in development mode - using local API server');
    return 'http://localhost:3001';
  }

  // 2) Auto-detect by hostname
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    // Local/dev
    if (host === 'localhost' || host === '127.0.0.1') return 'http://localhost:3001';

    // Any *.masoathu.org in production/staging → always use backend subdomain
    if (host.endsWith('masoathu.org')) return '';
  }

  // 3) Fallback (safe default)
  return '';
}

function normalizeToApi(base: string): string {
  const trimmed = base.replace(/\/+$/, '');
  return /\/api$/i.test(trimmed) ? trimmed : `${trimmed}/api`;
}



const RAW_BASE = resolveRawBase();
const API_BASE_URL = normalizeToApi(RAW_BASE);

// Log the API base URL with environment information
const isDev = process.env.NODE_ENV === 'development';
const isProd = process.env.NODE_ENV === 'production';
console.log(`🔌 API Base URL: ${API_BASE_URL} (${isDev ? 'DEVELOPMENT' : isProd ? 'PRODUCTION' : 'TEST'} mode)`);

// Add additional warning if using localhost in production (should never happen)
if (isProd && API_BASE_URL.includes('localhost')) {
  console.warn('⚠️ WARNING: Using localhost API in production mode!');
}

const BACKEND_API_BASE = API_BASE_URL;


// Auth mode: 'token' (default) or 'cookie'
const AUTH_MODE = (process.env.REACT_APP_API_AUTH_MODE || 'token').toLowerCase();
const USE_COOKIES = AUTH_MODE === 'cookie';


function fixWrongHost(config: AxiosRequestConfig) {
  if (typeof window === 'undefined') return config;

  // Build the full URL as axios will use it
  const full = new URL(
    (config.url || ''),
    (config.baseURL || BACKEND_API_BASE)
  );

  // If it's wrongly pointing at the FRONTEND origin (/api/...), rewrite to the backend
  const isFrontendApi = (full.origin === window.location.origin) && full.pathname.startsWith('/api/');
  if (isFrontendApi) {
    // We already have BACKEND_API_BASE like https://backend.masoathu.org/api
    // So keep only the path after /api
    const pathAfterApi = full.pathname.replace(/^\/api/, '');
    config.baseURL = BACKEND_API_BASE;
    config.url = pathAfterApi + full.search; // axios will join baseURL + url
  }

  return config;
}

// -------------------------------
// Axios instance
// -------------------------------
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // Increased timeout to 60 seconds for slow endpoints
  withCredentials: USE_COOKIES, // true only if you actually use cookies/sessions
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token if present (safe in both modes)

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Auto-correct any accidental calls to the frontend host
    config = fixWrongHost(config) as InternalAxiosRequestConfig;

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (token) {
      if (!config.headers) config.headers = {} as import('axios').AxiosRequestHeaders;
      // Set the Authorization header safely
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Centralized error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: any) => {
    const method = error?.config?.method?.toUpperCase?.() || 'UNKNOWN';
    const urlPath = error?.config?.url || 'unknown-endpoint';

    if (error?.code === 'ERR_NETWORK') {
      error.message = 'Network connection error. Please check your internet connection and try again.';
    } else if (error?.response?.status === 503) {
      error.serviceUnavailable = true;
      error.message = error.response?.data?.error || 'Server is temporarily unavailable. Please try again later.';
    } else if (error?.response?.status === 401 && !USE_COOKIES) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    logError(error, {
      component: 'API Client',
      action: `${method} ${urlPath}`,
      additionalInfo: { status: error?.response?.status, baseURL: API_BASE_URL, authMode: AUTH_MODE },
    });

    return Promise.reject(error);
  }
);

// -------------------------------
// API endpoints (unchanged)
// -------------------------------
export const api = {
  auth: {
    login: (phone: string, password: string) =>
      apiClient.post('/auth/login', { phone, password }),
    me: () => apiClient.get('/auth/me'),
    logout: () => apiClient.post('/auth/logout'),
  },

  users: {
    getAll: (params?: any) => apiClient.get('/users', { params }),
    getOne: (id: string) => apiClient.get(`/users/${id}`),
    create: (data: any) => apiClient.post('/users', data),
    update: (id: string, data: any) => apiClient.put(`/users/${id}`, data),
    delete: (id: string) => apiClient.delete(`/users/${id}`),
    getStats: () => apiClient.get('/users/stats/overview'),
  },

  roles: {
    getAll: (params?: any) => apiClient.get('/roles', { params }),
    getOne: (id: string) => apiClient.get(`/roles/${id}`),
    create: (data: any) => apiClient.post('/roles', data),
    update: (id: string, data: any) => apiClient.put(`/roles/${id}`, data),
    delete: (id: string) => apiClient.delete(`/roles/${id}`),
    getStats: () => apiClient.get('/roles/stats/overview'),
  },

  modules: {
    getAll: (params?: any) => apiClient.get('/modules', { params }),
    getOne: (id: string) => apiClient.get(`/modules/${id}`),
    create: (data: any) => apiClient.post('/modules', data),
    update: (id: string, data: any) => apiClient.put(`/modules/${id}`, data),
    delete: (id: string) => apiClient.delete(`/modules/${id}`),
    addPermission: (id: string, data: any) => apiClient.post(`/modules/${id}/permissions`, data),
    deletePermission: (id: string, permissionId: string) => apiClient.delete(`/modules/${id}/permissions/${permissionId}`),
    getStats: () => apiClient.get('/modules/stats/overview'),
  },

  permissions: {
    getAll: () => apiClient.get('/permissions'),
    getRolePermissions: (roleId: string) => apiClient.get(`/permissions/roles/${roleId}`),
    updateRolePermissions: (roleId: string, data: any) => apiClient.put(`/permissions/roles/${roleId}`, data),
    grantPermission: (roleId: string, data: any) => apiClient.post(`/permissions/roles/${roleId}/grant`, data),
    revokePermission: (roleId: string, data: any) => apiClient.post(`/permissions/roles/${roleId}/revoke`, data),
    getUserPermissions: (userId: string) => apiClient.get(`/permissions/user/${userId}`),
  },

  reports: {
    create: (data: any) => apiClient.post('/reports', data),
    getAll: (params?: any) => apiClient.get('/reports', { params }),
    getOne: (id: string) => apiClient.get(`/reports/${id}`),
    updateStatus: (id: string, status: string) =>
      apiClient.put(`/reports/${id}/status`, { status }),
    delete: (id: string) => apiClient.delete(`/reports/${id}`),
    getStats: (params?: any) => apiClient.get('/reports/stats/overview', { params }),
    getUserReports: (userId: string, params?: any) => apiClient.get(`/reports/user/${userId}`, { params }),
  },

  incidents: {
    getAll: (params?: any) => apiClient.get('/incidents', { params }),
    getOne: (id: string) => apiClient.get(`/incidents/${id}`),
    getCategories: () => apiClient.get('/incidents/categories'),
    updateStatus: (id: string, status: string, comment?: string) =>
      apiClient.put(`/incidents/${id}/status`, {
        status,
        ...(comment && { comment1: comment })
      }),
    update: (id: string, data: any) => apiClient.put(`/incidents/${id}`, data),
    delete: (id: string) => apiClient.delete(`/incidents/${id}`),
    getStats: (params?: any) => apiClient.get('/incidents/stats/overview', { params }),
    getUserIncidents: (userId: string, params?: any) => apiClient.get(`/incidents/user/${userId}`, { params }),
  },

  sms: {
    getAll: (params?: any) => apiClient.get('/sms', { params }),
    reprocess: (id: string) => apiClient.post(`/sms/${id}/reprocess`),
    getStats: (params?: any) => apiClient.get('/sms/stats/overview', { params }),
  },

  dashboard: {
    getOverview: (params?: {
      district?: string;
      constituency?: string;
      start_date?: string;
      end_date?: string;
      ward?: string;
      include_all_polling?: '0' | '1';
      district_id?: number;
      constituency_id?: number;
      ward_id?: number;
    }) => apiClient.get('/dashboard/overview', { params }),
    getRecentActivity: (params?: any) => apiClient.get('/dashboard/recent-activity', { params }),
    getGeographicDistribution: (params?: any) => apiClient.get('/dashboard/geographic-distribution', { params }),
    getPollingStations: (params?: any) => apiClient.get('/dashboard/polling-stations', {
      params,
      timeout: 180000 // 3 minutes timeout for this specific endpoint
    }),
    getCompliance: (params?: any) => apiClient.get('/dashboard/compliance', { params }),
    getTimeline: (params?: any) => apiClient.get('/dashboard/timeline', { params }),
    getAlerts: () => apiClient.get('/dashboard/alerts'),
    getPerformance: () => apiClient.get('/dashboard/performance'),
    getIncidentsMap: (params?: any) => apiClient.get('/dashboard/incidents-map', { params }),
    getObserverProgress: () => apiClient.get('/dashboard/observer-progress'),
    getDistrictCompliance: () => apiClient.get('/dashboard/district-compliance'),
    getCentersReportingStatus: (params?: {
      district_id?: number;
      timeline?: 'morning' | 'afternoon' | 'vote_count';
    }) => apiClient.get('/dashboard/centers-reporting-status', { params }),
    getDistrictWardCompliance: (districtName: string) =>
      apiClient.get(`/dashboard/district-ward-compliance?district_name=${encodeURIComponent(districtName)}`),
  },

  locations: {
    importExcel: (file: File, opts?: { dryRun?: boolean; allowDuplicates?: boolean; sampleLimit?: number | 'all' }) => {
      const form = new FormData();
      form.append('file', file);
      const params = { 
        dryRun: opts?.dryRun ? 'true' : 'false',
        allowDuplicates: opts?.allowDuplicates ? 'true' : 'false',
        ...(opts?.sampleLimit !== undefined ? { sampleLimit: String(opts.sampleLimit) } : {}),
      } as any;
      return apiClient.post('/locations/import-excel', form, {
        params,
        headers: { 'Content-Type': 'multipart/form-data' },
        maxBodyLength: 10 * 1024 * 1024,
      });
    },
    diagnoseWards: (formData: FormData) => {
      return apiClient.post('/locations/diagnose-wards', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxBodyLength: 10 * 1024 * 1024,
      });
    },
  },
};

// Optional helper to manipulate token
export function setAuthToken(token?: string) {
  if (token) localStorage.setItem('token', token);
  else localStorage.removeItem('token');
}